import { useState, useEffect, useCallback } from "react";
import { securityApi, type SecurityStatus, type SecurityAlert } from "@/services/security";

const SEVERITY_STYLES: Record<string, string> = {
  critical: "bg-accent-500 text-background-50",
  high: "bg-accent-200 text-accent-900",
  medium: "bg-secondary-200 text-secondary-900",
  low: "bg-background-200 text-foreground-700",
};

const CATEGORY_STYLES: Record<string, string> = {
  spam: "bg-secondary-100 text-secondary-900",
  integrity: "bg-accent-100 text-accent-900",
};

function timeAgo(value: string | null | undefined): string {
  if (!value) return "—";
  const sec = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 1000));
  if (sec < 60) return "just now";
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

export default function AdminSecurity() {
  const [status, setStatus] = useState<SecurityStatus | null>(null);
  const [alerts, setAlerts] = useState<SecurityAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [filter, setFilter] = useState<{ status: string; category: string }>({
    status: "open",
    category: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [s, a] = await Promise.all([
        securityApi.status(),
        securityApi.alerts({ status: filter.status || undefined, category: filter.category || undefined }),
      ]);
      setStatus(s);
      setAlerts(a);
    } catch (e: any) {
      setError(e?.message || "Failed to load security data");
    } finally {
      setLoading(false);
    }
  }, [filter.status, filter.category]);

  useEffect(() => { load(); }, [load]);

  async function runScan() {
    setScanning(true);
    setError("");
    setNotice("");
    try {
      const result = await securityApi.scan();
      setNotice(`Scan complete — ${result.newAlerts} new alert(s) raised.`);
      await load();
    } catch (e: any) {
      setError(e?.message || "Scan failed");
    } finally {
      setScanning(false);
    }
  }

  async function resolveAlert(id: number, status: "resolved" | "dismissed") {
    setBusyId(id);
    try {
      await securityApi.resolve(id, status);
      await load();
    } catch (e: any) {
      setError(e?.message || "Failed to update alert");
    } finally {
      setBusyId(null);
    }
  }

  async function rebuildBaseline() {
    setScanning(true);
    setError("");
    setNotice("");
    try {
      const r = await securityApi.recreateBaseline();
      setNotice(`Integrity baseline rebuilt — ${r.count} files tracked.`);
      await load();
    } catch (e: any) {
      setError(e?.message || "Failed to rebuild baseline");
    } finally {
      setScanning(false);
    }
  }

  const int = status?.integrity;
  const integrityOk = int ? (int.ok === true) : null;
  const totalFiles = (int?.changed?.length || 0) + (int?.added?.length || 0) + (int?.removed?.length || 0);

  return (
    <div className="space-y-6">
      {error && <div className="rounded-2xl bg-accent-100 border border-accent-300 p-4 text-sm text-accent-900">{error}</div>}
      {notice && <div className="rounded-2xl bg-secondary-100 border border-secondary-200 p-4 text-sm text-secondary-900">{notice}</div>}
      {loading && <p className="py-16 text-center text-foreground-600"><i className="ri-loader-4-line animate-spin"></i> Loading...</p>}

      {/* Actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="text-sm text-foreground-600">
          Last scan: <span className="font-medium text-foreground-950">{timeAgo(status?.lastScan)}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={rebuildBaseline}
            disabled={scanning}
            className="flex items-center gap-2 px-4 py-2 rounded-full border border-background-300 text-sm font-medium text-foreground-800 hover:bg-background-100 whitespace-nowrap"
          >
            <i className="ri-refresh-line"></i> Rebuild Baseline
          </button>
          <button
            onClick={runScan}
            disabled={scanning}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-600 text-background-50 text-sm font-semibold hover:bg-primary-700 whitespace-nowrap"
          >
            <i className={`ri-radar-line ${scanning ? "animate-spin" : ""}`}></i>
            {scanning ? "Scanning..." : "Run Scan"}
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="rounded-2xl bg-background-50 border border-background-200 p-5">
          <p className="text-xs uppercase tracking-wider text-foreground-600">Open Alerts</p>
          <p className="mt-2 font-heading text-3xl font-bold text-foreground-950">{status?.counts?.open ?? 0}</p>
          <p className="mt-1 text-xs text-foreground-600">{status?.counts?.resolved ?? 0} resolved · {status?.counts?.dismissed ?? 0} dismissed</p>
        </div>
        <div className="rounded-2xl bg-background-50 border border-background-200 p-5">
          <p className="text-xs uppercase tracking-wider text-foreground-600">Critical / High</p>
          <p className="mt-2 font-heading text-3xl font-bold text-accent-700">{(status?.bySeverity?.critical ?? 0) + (status?.bySeverity?.high ?? 0)}</p>
          <p className="mt-1 text-xs text-foreground-600">{status?.bySeverity?.medium ?? 0} medium · {status?.bySeverity?.low ?? 0} low</p>
        </div>
        <div className="rounded-2xl bg-background-50 border border-background-200 p-5">
          <p className="text-xs uppercase tracking-wider text-foreground-600">Security Headers</p>
          <p className="mt-2 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs ${status?.headers?.csp ? "bg-secondary-200 text-secondary-900" : "bg-accent-100 text-accent-900"}`}>
              <i className={status?.headers?.csp ? "ri-check-line" : "ri-close-line"}></i>
            </span>
            Active
          </p>
          <p className="mt-1 text-xs text-foreground-600">CSP · X-Frame-Options · nosniff · Secure cookies</p>
        </div>
        <div className="rounded-2xl bg-background-50 border border-background-200 p-5">
          <p className="text-xs uppercase tracking-wider text-foreground-600">File Integrity</p>
          <p className="mt-2 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            {integrityOk === null ? (
              <span className="text-foreground-600">—</span>
            ) : integrityOk ? (
              <span className="flex items-center gap-2">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-secondary-200 text-secondary-900 text-xs"><i className="ri-check-line"></i></span>
                Clean
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-accent-500 text-background-50 text-xs"><i className="ri-alert-line"></i></span>
                {totalFiles} deviation{totalFiles === 1 ? "" : "s"}
              </span>
            )}
          </p>
          <p className="mt-1 text-xs text-foreground-600">{int?.tracked ?? 0} files tracked · {timeAgo(int?.lastCheckedAt)}</p>
        </div>
      </div>

      {/* Integrity detail */}
      {int && !int.ok && int.changed && (
        <div className="rounded-2xl bg-accent-100 border border-accent-300 p-5">
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-shield-keyhole-line text-accent-800"></i>
            <h3 className="font-heading text-sm font-semibold text-accent-900">Files deviating from the integrity baseline</h3>
          </div>
          <ul className="space-y-1.5 text-sm text-accent-900">
            {int.changed.map((c) => (
              <li key={c.path} className="flex items-center gap-2">
                <i className="ri-file-edit-line"></i> changed · <code className="text-xs">{c.path}</code>
              </li>
            ))}
            {int.added?.map((p) => (
              <li key={`a-${p}`} className="flex items-center gap-2"><i className="ri-file-add-line"></i> added · <code className="text-xs">{p}</code></li>
            ))}
            {int.removed?.map((p) => (
              <li key={`r-${p}`} className="flex items-center gap-2"><i className="ri-file-close-line"></i> removed · <code className="text-xs">{p}</code></li>
            ))}
          </ul>
          {!int.ok && int.error === "no-baseline" && <p className="text-sm text-accent-900 mt-1">Run a scan to create the baseline.</p>}
        </div>
      )}

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        {["open", "resolved", "dismissed"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter((f) => ({ ...f, status: s }))}
            className={`px-4 py-1.5 rounded-full text-sm font-medium capitalize whitespace-nowrap ${
              filter.status === s ? "bg-foreground-950 text-background-50" : "bg-background-50 border border-background-200 text-foreground-700 hover:bg-background-100"
            }`}
          >
            {s}
          </button>
        ))}
        <span className="w-px h-5 bg-background-200 mx-1"></span>
        {["", "spam", "integrity"].map((c) => (
          <button
            key={c || "all"}
            onClick={() => setFilter((f) => ({ ...f, category: c }))}
            className={`px-4 py-1.5 rounded-full text-sm font-medium capitalize whitespace-nowrap ${
              filter.category === c ? "bg-foreground-950 text-background-50" : "bg-background-50 border border-background-200 text-foreground-700 hover:bg-background-100"
            }`}
          >
            {c === "" ? "All" : c}
          </button>
        ))}
      </div>

      {/* Alerts table */}
      <div className="rounded-2xl bg-background-50 border border-background-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-100 text-left text-xs uppercase tracking-wider text-foreground-600">
                <th className="px-4 py-2.5">Severity</th>
                <th className="px-4 py-2.5">Alert</th>
                <th className="px-4 py-2.5">Source</th>
                <th className="px-4 py-2.5">Pattern</th>
                <th className="px-4 py-2.5">Detected</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-100">
              {alerts.map((a) => (
                <tr key={a.id}>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${SEVERITY_STYLES[a.severity] || "bg-background-200 text-foreground-700"}`}>
                      {a.severity}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase ${CATEGORY_STYLES[a.category] || "bg-background-200 text-foreground-700"}`}>{a.category}</span>
                      <div>
                        <p className="font-medium text-foreground-950">{a.title}</p>
                        <p className="text-xs text-foreground-600 max-w-md truncate">{a.snippet}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-foreground-700">{a.source}{a.entity_id ? ` #${a.entity_id}` : ""}</td>
                  <td className="px-4 py-3 text-xs text-foreground-600">{a.matched_pattern}</td>
                  <td className="px-4 py-3 text-xs text-foreground-600">{timeAgo(a.created_at)}</td>
                  <td className="px-4 py-3">
                    {a.status === "open" ? (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => resolveAlert(a.id, "resolved")}
                          disabled={busyId === a.id}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-secondary-200 text-secondary-900 text-xs font-semibold hover:bg-secondary-300 whitespace-nowrap"
                        >
                          <i className="ri-check-double-line"></i> Resolve
                        </button>
                        <button
                          onClick={() => resolveAlert(a.id, "dismissed")}
                          disabled={busyId === a.id}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-background-300 text-foreground-700 text-xs font-medium hover:bg-background-100 whitespace-nowrap"
                        >
                          <i className="ri-close-line"></i> Dismiss
                        </button>
                      </div>
                    ) : (
                      <span className="flex items-center justify-end gap-1.5 text-xs text-foreground-600">
                        <i className={a.status === "resolved" ? "ri-check-line" : "ri-eye-off-line"}></i> {a.status}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {alerts.length === 0 && !loading && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-foreground-600">No alerts in this view.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}